// "Forgot password?" (J-246, the owner's order item 4b): the email address, then the same message
// whether or not an account uses it. Better Auth's /request-password-reset mails a link to
// /reset-password that works once, within one hour. The address is never logged (T12 rule 1).
// J-257: where the server has no mail transport (production), the page says to ask an administrator,
// shows no form and sends no request; the server refuses a direct request there too.
import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@vbltc/ui/button";
import { Input } from "@vbltc/ui/input";
import { StatusRegion } from "@vbltc/ui/components/status-region/status-region";
import { AUTH_BUTTON_CLASS, AUTH_INPUT_CLASS, AUTH_LABEL_CLASS, AUTH_LINK_CLASS } from "@vbltc/ui";
import { AuthPage } from "../app/auth-page";
import { authClient } from "../features/auth/client";
import { formString } from "../features/auth/form-value";
import { loadAuthOptions, RESET_UNAVAILABLE_MESSAGE } from "../features/auth/api/auth-options";
import { log } from "../telemetry";

export const Route = createFileRoute("/forgot-password")({
  loader: ({ context }) => loadAuthOptions(context.queryClient),
  component: ForgotPasswordPage,
});

/** Better Auth's code when no reset link can be sent (`plugins/auth.ts`, J-257). */
const RESET_PASSWORD_DISABLED = "RESET_PASSWORD_DISABLED";

/** The one answer to every accepted request: it says nothing about whether the account exists. */
export const RESET_REQUESTED_MESSAGE =
  "If a user has that address, a reset link is on its way. The link works once, within one hour.";

function requestError(status: number, code: string | undefined): string {
  if (code === RESET_PASSWORD_DISABLED) return RESET_UNAVAILABLE_MESSAGE;
  if (status === 429)
    return "Too many reset requests from this network. Wait five minutes, then try again.";
  if (status === 400) return "Enter the email address you sign in with.";
  return "The request could not be sent. Try again in a minute.";
}

function ForgotPasswordPage() {
  // Only a known "no transport" hides the form; when the read failed, the server's answer decides.
  const unavailable = Route.useLoaderData()?.passwordResetByEmail === false;
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    const email = formString(new FormData(e.currentTarget), "email");
    const { error: err } = await authClient.requestPasswordReset({ email });
    setPending(false);
    if (err) {
      log.debug("reset request rejected", err.status); // status only; never the address
      setError(requestError(err.status, err.code));
      return;
    }
    setSent(true);
  }

  if (unavailable)
    return (
      <AuthPage heading="Reset your password">
        <StatusRegion
          tone="moderate"
          message={RESET_UNAVAILABLE_MESSAGE}
          className="mb-4.5 rounded-md px-4"
        />
        <Link to="/login" className={AUTH_LINK_CLASS} data-telemetry="forgot-password-back">
          Back to sign in
        </Link>
      </AuthPage>
    );

  return (
    <AuthPage
      heading="Reset your password"
      description="Enter the email address you sign in with. We will send you a link to choose a new password."
    >
      {sent ? (
        <StatusRegion
          tone="success"
          message={RESET_REQUESTED_MESSAGE}
          className="mb-4.5 rounded-md px-4"
        />
      ) : null}
      <form
        onSubmit={(e) => void onSubmit(e)}
        aria-label="Reset your password"
        className="flex flex-col gap-4.5"
      >
        <label className={AUTH_LABEL_CLASS}>
          Email
          <Input
            name="email"
            type="email"
            autoComplete="username"
            required
            className={AUTH_INPUT_CLASS}
          />
        </label>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={pending}
          data-telemetry="forgot-password-submit"
          className={AUTH_BUTTON_CLASS}
        >
          Send reset link
        </Button>
        <Link to="/login" className={AUTH_LINK_CLASS} data-telemetry="forgot-password-back">
          Back to sign in
        </Link>
      </form>
    </AuthPage>
  );
}
